import { formatCurrency } from "@ca/formatters/currency";

/** Pure premium math — safe for browser bundles (no `@ca/db`). */

export function scalePremiumByLineSize (baseTotal: number, lineSize: number | null | undefined): number {
	const ls = lineSize ?? 100;
	return baseTotal * (ls / 100);
}

/** Line-size-scaled invoiced premium from a 100% program amount (pro-rata or full-term). */
export function invoicedPremiumFromProgramAmount (
	programAmount: number,
	lineSize: number | null | undefined,
): number {
	return roundCurrency2(scalePremiumByLineSize(programAmount, lineSize));
}

/** Wording for mid-term aircraft endorsement forms; amounts are line-size scaled like invoices. */
export function midTermPremiumConsiderationForDoc (
	rawAdjustment: number,
	lineSize: number | null | undefined,
): string {
	const scaled = scalePremiumByLineSize(rawAdjustment, lineSize);
	if (scaled === 0) {
		return "the included premium";
	}
	if (scaled < 0) {
		return `a return premium of ${formatCurrency(scaled)}`;
	}
	return `an additional premium of ${formatCurrency(scaled)}`;
}

/** Wording for mid-term forms when the invoiced amount is already known. */
export function midTermPremiumConsiderationFromInvoiced (invoicedAmount: number): string {
	if (invoicedAmount === 0) {
		return "the included premium";
	}
	if (invoicedAmount < 0) {
		return `a return premium of ${formatCurrency(invoicedAmount)}`;
	}
	return `an additional premium of ${formatCurrency(invoicedAmount)}`;
}

const PRO_RATA_SHARE_DECIMALS = 3;
const PRO_RATA_SHARE_FACTOR = 10 ** PRO_RATA_SHARE_DECIMALS;

/** Pro-rata share and derived factors (3 decimal places). */
export function roundProRataShare (n: number): number {
	return Math.round(n * PRO_RATA_SHARE_FACTOR) / PRO_RATA_SHARE_FACTOR;
}

export type ProRataTermDetails = {
	/** Unearned days from the cancellation date through end of term (the cancellation date itself is unearned). */
	remainingDays: number;
	/** Term length in days; a full annual term is a fixed 365-day year. */
	totalDays: number;
	/** `remainingDays / totalDays`, rounded to 3 decimal places. */
	share: number;
};

const parseYmdUtc = (s: string) => {
	const [y, m, d] = s.trim().split("T")[0].split("-").map(Number);
	return Date.UTC(y, m - 1, d);
};

/**
 * Days remaining and pro-rata share (3 dp) on a 365-day year.
 * Delegates to `segmentUnearnedProRataShareDetails` so display and applied refund stay consistent.
 * Use `roundCurrency2(premium * share)` for money.
 */
export function proRataRemainingShareDetails (
	policyStartYmd: string,
	policyEndYmd: string,
	effectiveYmd: string,
): ProRataTermDetails {
	return segmentUnearnedProRataShareDetails(policyStartYmd, policyEndYmd, effectiveYmd);
}

/** Pro-rata share only (3 decimal places). */
export function proRataRemainingShare (
	policyStartYmd: string,
	policyEndYmd: string,
	effectiveYmd: string,
): number {
	return proRataRemainingShareDetails(policyStartYmd, policyEndYmd, effectiveYmd).share;
}

/** Currency amount rounded to cents (2 decimal places). */
export function roundCurrency2 (n: number): number {
	return Math.round(n * 100) / 100;
}

/**
 * Unearned (return-premium) share for a premium segment running `segmentStartYmd` → policy `policyEndYmd`.
 * Earned days run from the segment/inception day up to (not including) the cancellation date, so a same-day
 * cancellation earns nothing and cancelling the next day earns one day.
 * A full annual term (365 days, or 366 when it spans Feb 29) is billed as a fixed 365-day year.
 */
export function segmentUnearnedProRataShareDetails (
	segmentStartYmd: string,
	policyEndYmd: string,
	cancellationYmd: string,
): ProRataTermDetails {
	const tSeg = parseYmdUtc(segmentStartYmd);
	const tEnd = parseYmdUtc(policyEndYmd);
	const tEff = parseYmdUtc(cancellationYmd);
	const spanDays = Math.round((tEnd - tSeg) / 86400000);
	const totalDays = Math.max(1, spanDays === 366 ? 365 : spanDays);
	if (tEff < tSeg) {
		return {
			remainingDays: totalDays, totalDays, share: 1,
		};
	}
	if (tEff >= tEnd) {
		return {
			remainingDays: 0, totalDays, share: 0,
		};
	}
	const earnedDays = Math.round((tEff - tSeg) / 86400000);
	const remainingDays = Math.max(0, totalDays - earnedDays);
	const share = roundProRataShare(remainingDays / totalDays);
	return {
		remainingDays, totalDays, share,
	};
}

/**
 * How cancellation return premium scales vs pro-rata unearned share.
 * Aligns with common short-rate / pro-rata tools (e.g. Commonwealth “wheel”): pro rata = full unearned factor;
 * “short rate (90% pro rata)” = 90% of the pro-rata unearned factor; plain “short rate” applies a steeper penalty.
 */
export type CancellationFactorMethod = "pro_rata" | "short_rate" | "short_rate_90_pro_rata";
export type MidTermPremiumFactorMethod = CancellationFactorMethod | "flat_rate";

export function cancellationUnearnedFactor (
	method: CancellationFactorMethod,
	segmentStartYmd: string,
	policyEndYmd: string,
	cancellationYmd: string,
): number {
	const pr = segmentUnearnedProRataShareDetails(segmentStartYmd, policyEndYmd, cancellationYmd).share;
	if (method === "pro_rata") {
		return pr;
	}
	if (method === "short_rate_90_pro_rata") {
		return roundProRataShare(pr * 0.9);
	}
	// short_rate — more punitive than 90% pro rata when a full short-rate table is not available
	return roundProRataShare(pr * 0.85);
}

export function midTermPremiumFactor (
	method: MidTermPremiumFactorMethod,
	segmentStartYmd: string,
	policyEndYmd: string,
	effectiveYmd: string,
): number {
	if (method === "flat_rate") {
		return 1;
	}
	return cancellationUnearnedFactor(method, segmentStartYmd, policyEndYmd, effectiveYmd);
}

export type CancellationBillingSegment = {
	segmentEffectiveYmd: string;
	premium: number;
	mexicanFee: number;
	stateSurcharge: number;
	policyFee: number;
};

/**
 * Invoice line types that count as **gross premium** for cancellation (Wilbur `invoice_lines.type`).
 * Fees, broker commission, and other lines must never be rolled into this bucket.
 */
export const CANCELLATION_PREMIUM_LINE_TYPES = ["inception_premium", "endorsement"] as const;

export type CancellationPremiumInvoiceLineType = (typeof CANCELLATION_PREMIUM_LINE_TYPES)[number];

export function isCancellationPremiumInvoiceLineType (type: string): type is CancellationPremiumInvoiceLineType {
	return (CANCELLATION_PREMIUM_LINE_TYPES as readonly string[]).includes(type);
}

/** Sum only inception premium + endorsement lines (excludes fees, broker commission, etc.). */
export function sumCancellationPremiumFromInvoiceLines (
	lines: Array<{ type: string; amount: number }>,
): number {
	let premium = 0;
	for (const line of lines) {
		if (isCancellationPremiumInvoiceLineType(line.type)) {
			premium += line.amount;
		}
	}
	return premium;
}

function partitionCancellationInvoiceLineAmounts (lines: Array<{ type: string; amount: number }>): {
	premium: number;
	mexicanFee: number;
	stateSurcharge: number;
	policyFee: number;
} {
	let premium = 0;
	let mexicanFee = 0;
	let stateSurcharge = 0;
	let policyFee = 0;
	for (const line of lines) {
		const t = line.type;
		if (isCancellationPremiumInvoiceLineType(t)) {
			premium += line.amount;
			continue;
		}
		if (t === "mexican_certificate") {
			mexicanFee += line.amount;
			continue;
		}
		if (t === "state_fee") {
			stateSurcharge += line.amount;
			continue;
		}
		if (t === "policy_fee") {
			policyFee += line.amount;
			continue;
		}
		// broker_commission, policy_cancellation, unknown: do not allocate to premium or fees here
	}
	return {
		premium, mexicanFee, stateSurcharge, policyFee, 
	};
}

export type CancellationEndorsementRow = {
	slug: string;
	invoiceId?: string | null;
	aircraftId?: string | null;
	effectiveDate?: string | null;
	createdAt?: string | Date | null;
	data?: unknown;
};

const MID_TERM_AIRCRAFT_SLUGS = new Set(["additional_aircraft", "adjusted_aircraft", "removed_aircraft"]);

function isEmptyCancellationSegment (s: CancellationBillingSegment): boolean {
	return Math.abs(s.premium) < 0.005
		&& Math.abs(s.mexicanFee) < 0.005
		&& Math.abs(s.stateSurcharge) < 0.005
		&& Math.abs(s.policyFee) < 0.005;
}

function numberFieldFromData (data: unknown, key: string): number | null {
	const d = data as Record<string, unknown> | null | undefined;
	const v = d?.[key];
	return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function snapshotAnnualPremiumFromData (data: unknown, key: "before" | "after"): number | null {
	const d = data as Record<string, unknown> | null | undefined;
	return numberFieldFromData(d?.[key], "totalPremium");
}

function billedMidTermAircraftPremiumFromData (data: unknown, lineSize: number | null | undefined): number {
	const invoiced = numberFieldFromData(data, "invoicedPremium");
	if (invoiced != null) {
		return invoiced;
	}
	const adjustment = numberFieldFromData(data, "premiumAdjustment");
	return adjustment == null ? 0 : roundCurrency2(scalePremiumByLineSize(adjustment, lineSize));
}

function effectiveYmdFromRow (row: CancellationEndorsementRow): string {
	return row.effectiveDate?.trim().split("T")[0] ?? "";
}

function compareEndorsementRowsChronologically (a: CancellationEndorsementRow, b: CancellationEndorsementRow): number {
	const effA = effectiveYmdFromRow(a);
	const effB = effectiveYmdFromRow(b);
	if (effA !== effB) {
		return effA < effB ? -1 : 1;
	}
	const createdA = a.createdAt == null ? 0 : (new Date(a.createdAt).getTime() || 0);
	const createdB = b.createdAt == null ? 0 : (new Date(b.createdAt).getTime() || 0);
	return createdA - createdB;
}

function backOutRemovedAircraftPremium (args: {
	inception: CancellationBillingSegment;
	segmentsByInvoiceId: Map<string, CancellationBillingSegment>;
	endorsements: CancellationEndorsementRow[];
	lineSize: number | null | undefined;
}): void {
	const rowsByAircraft = new Map<string, CancellationEndorsementRow[]>();
	for (const row of args.endorsements) {
		if (!row.aircraftId || !MID_TERM_AIRCRAFT_SLUGS.has(row.slug)) {
			continue;
		}
		const rows = rowsByAircraft.get(row.aircraftId) ?? [];
		rows.push(row);
		rowsByAircraft.set(row.aircraftId, rows);
	}

	const subtractFromSegment = (invoiceId: string | null | undefined, amount: number) => {
		const seg = invoiceId ? args.segmentsByInvoiceId.get(invoiceId) : undefined;
		if (seg) {
			seg.premium = roundCurrency2(seg.premium - amount);
		}
	};

	for (const rows of rowsByAircraft.values()) {
		rows.sort(compareEndorsementRowsChronologically);
		let onInceptionSchedule = true;
		let inceptionAnnualPremium: number | null = null;
		let billedContributions: Array<{ invoiceId: string | null | undefined; amount: number }> = [];
		for (const row of rows) {
			const billed = billedMidTermAircraftPremiumFromData(row.data, args.lineSize);
			if (row.slug === "additional_aircraft") {
				onInceptionSchedule = false;
				inceptionAnnualPremium = null;
				billedContributions = [{ invoiceId: row.invoiceId, amount: billed }];
				continue;
			}
			if (row.slug === "adjusted_aircraft") {
				if (onInceptionSchedule && inceptionAnnualPremium == null) {
					inceptionAnnualPremium = snapshotAnnualPremiumFromData(row.data, "before");
				}
				billedContributions.push({ invoiceId: row.invoiceId, amount: billed });
				continue;
			}
			for (const c of billedContributions) {
				subtractFromSegment(c.invoiceId, c.amount);
			}
			if (onInceptionSchedule) {
				const annual = inceptionAnnualPremium ?? snapshotAnnualPremiumFromData(row.data, "before") ?? 0;
				const billedAtInception = roundCurrency2(scalePremiumByLineSize(annual, args.lineSize));
				args.inception.premium = Math.max(0, roundCurrency2(args.inception.premium - billedAtInception));
			}
			subtractFromSegment(row.invoiceId, billed);
			onInceptionSchedule = true;
			inceptionAnnualPremium = null;
			billedContributions = [];
		}
	}
}

export function buildCancellationBillingSegmentsFromPolicyInvoices (args: {
	policyStartYmd: string;
	policyEndYmd: string;
	lineSize: number | null | undefined;
	invoices: Array<{
		id?: string;
		qbInvoiceType: string;
		lines?: Array<{ type: string; amount: number }>;
	}>;
	endorsements: CancellationEndorsementRow[];
}): CancellationBillingSegment[] {
	const effectiveYmdByInvoiceId = new Map<string, string>();
	const aircraftInvoiceIds = new Set<string>();
	for (const row of args.endorsements) {
		if (!row.invoiceId) {
			continue;
		}
		const eff = effectiveYmdFromRow(row);
		if (eff && !effectiveYmdByInvoiceId.has(row.invoiceId)) {
			effectiveYmdByInvoiceId.set(row.invoiceId, eff);
		}
		if (MID_TERM_AIRCRAFT_SLUGS.has(row.slug)) {
			aircraftInvoiceIds.add(row.invoiceId);
		}
	}

	const inception: CancellationBillingSegment = {
		segmentEffectiveYmd: args.policyStartYmd,
		premium: 0,
		mexicanFee: 0,
		stateSurcharge: 0,
		policyFee: 0,
	};
	const segmentsByInvoiceId = new Map<string, CancellationBillingSegment>();
	for (const inv of args.invoices) {
		const id = inv.id;
		const isAircraftCreditMemo = inv.qbInvoiceType === "credit_memo" && id != null && aircraftInvoiceIds.has(id);
		if (inv.qbInvoiceType !== "invoice" && !isAircraftCreditMemo) {
			continue;
		}
		const amounts = partitionCancellationInvoiceLineAmounts(inv.lines ?? []);
		const eff = id ? effectiveYmdByInvoiceId.get(id) : undefined;
		if (!id || !eff) {
			inception.premium += amounts.premium;
			inception.mexicanFee += amounts.mexicanFee;
			inception.stateSurcharge += amounts.stateSurcharge;
			inception.policyFee += amounts.policyFee;
			continue;
		}
		segmentsByInvoiceId.set(id, {
			segmentEffectiveYmd: eff, ...amounts, 
		});
	}

	backOutRemovedAircraftPremium({
		inception,
		segmentsByInvoiceId,
		endorsements: args.endorsements,
		lineSize: args.lineSize,
	});

	return [inception, ...segmentsByInvoiceId.values()].filter(s => !isEmptyCancellationSegment(s));
}

export function computeCancellationRefundDefaults (args: {
	method: CancellationFactorMethod;
	policyEndYmd: string;
	cancellationYmd: string;
	segments: CancellationBillingSegment[];
	/** When no invoice segments, use this single premium total from policy inception (legacy). */
	fallbackPremiumBase?: number;
	fallbackSegmentStartYmd?: string;
	brokerCommissionRate: number;
}): {
		refundedPremium: number;
		refundedBrokerCommission: number;
		stateSurcharge: number;
		mexicanCertificate: number;
		policyFee: number;
	} {
	const segs = args.segments.length > 0
		? args.segments
		: (args.fallbackPremiumBase != null && args.fallbackSegmentStartYmd
			? [{
				segmentEffectiveYmd: args.fallbackSegmentStartYmd,
				premium: args.fallbackPremiumBase,
				mexicanFee: 0,
				stateSurcharge: 0,
				policyFee: 0,
			}]
			: []);

	let refundedPremium = 0;
	let stateTotal = 0;
	let mexicanTotal = 0;
	let policyFeeTotal = 0;

	for (const s of segs) {
		const f = cancellationUnearnedFactor(
			args.method,
			s.segmentEffectiveYmd,
			args.policyEndYmd,
			args.cancellationYmd,
		);
		refundedPremium += s.premium * f;
		stateTotal += s.stateSurcharge * f;
		mexicanTotal += s.mexicanFee * f;
		policyFeeTotal += s.policyFee * f;
	}

	const rp = roundCurrency2(refundedPremium);
	const br = roundCurrency2(Math.abs(rp) * args.brokerCommissionRate);
	return {
		refundedPremium: rp,
		refundedBrokerCommission: br,
		stateSurcharge: roundCurrency2(stateTotal),
		mexicanCertificate: roundCurrency2(mexicanTotal),
		policyFee: roundCurrency2(policyFeeTotal),
	};
}

export function endorsementAdditionalPremiumFromData (data: unknown): number {
	const d = data as Record<string, unknown>;
	if (d.slug === "deletion") {
		const ap = d.deletionAdditionalPremium;
		return typeof ap === "number" ? ap : 0;
	}
	const ap = d.additionalPremium;
	return typeof ap === "number" ? ap : 0;
}
